/**
 * The one place the frontend logs from (GOD-59) – ESLint forbids `console`
 * everywhere else. Warnings and errors go to the browser console; errors are
 * also handed to a reporter (Analytics "exception", registered in
 * services/firebase.ts) so problems in production become visible.
 * No imports on purpose: firebase.ts uses the logger itself.
 */
export type ErrorReporter = (context: string, description: string, fatal: boolean) => void;

let reporter: ErrorReporter | null = null;

export function setErrorReporter(next: ErrorReporter | null): void {
    reporter = next;
}

/** Short, PII-free description: error code and message, capped. */
export function describeError(error: unknown): string {
    if (error && typeof error === "object") {
        const code = "code" in error ? String((error as { code: unknown }).code) : "";
        const message = "message" in error ? String((error as { message: unknown }).message) : "";
        if (code || message) return [code, message].filter(Boolean).join(" - ").slice(0, 300);
    }
    return String(error).slice(0, 300);
}

function report(context: string, error: unknown, fatal: boolean) {
    try {
        reporter?.(context, describeError(error), fatal);
    } catch {
        // Reporting must never break the app.
    }
}

export const logger = {
    warn(context: string, detail?: unknown): void {
        // eslint-disable-next-line no-console
        console.warn(`[${context}]`, ...(detail === undefined ? [] : [detail]));
    },
    error(context: string, error?: unknown, options: { fatal?: boolean } = {}): void {
        // eslint-disable-next-line no-console
        console.error(`[${context}]`, ...(error === undefined ? [] : [error]));
        report(context, error, options.fatal ?? false);
    },
};
